import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Signal, viewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ApiPaths } from '@uigy/app.settings';
import { CardBoxComponent } from '@uigy/shared/components';
import { ToasterType } from '@uigy/shared/components/toaster/enums/toaster-type.enum';
import { ToasterService } from '@uigy/shared/components/toaster/services/toaster.service';
import { UploadFileComponent } from '@uigy/shared/components/upload-file/upload-file.component';
import {
  MAX_FILE_SIZE_VALIDATOR_KEY,
  MaxFileSizeValidatorDirective,
} from '@uigy/shared/directives/max-file-size-validator.directive';
import { HttpRequestMethod } from '@uigy/shared/enums/http-request-method';
import { LanguageService } from '@uigy/shared/services/language.service';
import { UploadFileService } from '@uigy/shared/services/upload-file/upload-file.service';

import { UIButtonComponent } from '@uigy/shared/components/ui-button/ui-button.component';
import { AppIdDirective } from '@uigy/shared/directives/app-id/app-id.directive';
import { AppIdsEnum } from '@uigy/shared/directives/app-id/appIds.enum';
import { EMPTY } from 'rxjs';
import { catchError, finalize, take } from 'rxjs/operators';
import { Capability } from '@uigy/generated/graphql';

@Component({
  selector: 'app-project-files',
  imports: [

    AppIdDirective,
    UploadFileComponent,
    MaxFileSizeValidatorDirective,
    UIButtonComponent,
    CardBoxComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './project-files.component.html',
  styleUrl: './project-files.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectFilesComponent {
  public fileUpload: Signal<UploadFileComponent | undefined> = viewChild('projectFilesUpload', { read: UploadFileComponent });
  public readonly languageService = inject(LanguageService);
  public readonly uploadFileService = inject(UploadFileService);
  public readonly toasterService = inject(ToasterService);
  public readonly cdr = inject(ChangeDetectorRef);

  public readonly projectFilesTexts = this.languageService.texts.settings.env_config.asset_settings.project_files;
  public readonly generalTexts = this.languageService.texts.general;
  public fileControl = new FormControl();
  protected readonly AppIdsEnum = AppIdsEnum;
  public isLoading = false;


  public onFileChanged(uploadFileComponent: UploadFileComponent): void {
    const file: File | undefined = this.fileControl.value.get('file');
    if (!file) {
      return;
    }

    if (!this.fileControl.valid) {
      if (this.fileControl.errors && Object.keys(this.fileControl.errors).includes(MAX_FILE_SIZE_VALIDATOR_KEY)) {
        const invalidMsg = this.projectFilesTexts.max_size;
        this.toasterService.openToaster({ message: invalidMsg, type: ToasterType.Error });
      }
      return;
    }

    this.uploadFile(file, uploadFileComponent);
  }

  public openFileUploadDialog(): void {
    this.fileUpload()?.fileInputHtmlElement.nativeElement.click();
  }

  private uploadFile(file: File, uploadFileComponent: UploadFileComponent): void {
    this.isLoading = true;
    const formData = new FormData();
    formData.append('file', file);

    this.uploadFileService.uploadFile<any>(ApiPaths.uploadProjectFiles, HttpRequestMethod.PUT, {
      body: formData,
    })
      .pipe(
        take(1),
        catchError(() => {
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
          // Reset file input value to allow re-uploading the same file after an error
          uploadFileComponent.fileInputHtmlElement.nativeElement.value = '';
          // Reset form control to ensure clean state
          this.fileControl.setValue(null);
          this.cdr.detectChanges();
        }),
      ).subscribe(() => {
      this.toasterService.openToaster({
        type: ToasterType.Success,
        message: this.projectFilesTexts.upload_success,
      });
      this.cdr.detectChanges();
    });
  }


  protected readonly Capability = Capability;
}
